/**
 * @fileoverview Target overdue/completion derivation.
 * @description Every spec is explicit that Overdue is a derived subset of Outstanding ("these
 * figures must not be added together"), not an independent status value, and that Lost/On
 * Hold/archived targets stay in tracking and can still become Overdue. This file is the one place
 * that computes "is this target overdue right now", so every page/endpoint derives it the same way
 * instead of each re-implementing (and risking drift in) the comparison.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { Target } from '@/features/sales/types/tenant.types'

/**
 * @description Determines whether an outstanding target's original end date has passed. A
 * Completed target is never overdue, regardless of its end date — "Completed" and "Overdue" are
 * mutually exclusive outcomes of the same target.
 * @param {Pick<Target, 'status' | 'endDate'>} target - The target's status and original end date.
 * @param {Date} [now] - The reference time to compare against (defaults to the current time;
 * overridable for deterministic tests).
 * @returns {boolean} Whether the target is currently overdue.
 */
export function isTargetOverdue(
  target: Pick<Target, 'status' | 'endDate'>,
  now = new Date(),
): boolean {
  if (target.status !== 'Outstanding') {
    return false
  }
  return new Date(target.endDate).getTime() < now.getTime()
}
